import type { Dispatch, SetStateAction } from "react";
import type { DockSurfaceState, PopupSurfaceState } from "@/surfaces/SurfaceTypes";
import type {
	EngagementDockContentState,
	EngagementProviderResetReason,
} from "@/provider/EngagementProvider";
import type { ShellPopupPhase } from "@/shell/ShellRuntimeTypes";
import type { ActiveDockTarget } from "@/orchestrators/useCaptainUpIntegration";
import { getQueryParam } from "@/utils";

export type ShellDockHostMap = Partial<Record<ActiveDockTarget, HTMLDivElement | null>>;

export type ShellRuntimeState = {
	popupPhase: ShellPopupPhase;
	popupDesiredOpen: boolean;
	popupOpenIntentId: number;
	pendingPopupGeneration: number | null;
	freshBootRequiredBeforeNextOpen: boolean;

	modalHasContent: boolean;
	dockContentState: EngagementDockContentState;

	activeDockTarget: ActiveDockTarget;
	suppressNextProtocolCloseToken: number;

	dockState: DockSurfaceState | null;
	popupState: PopupSurfaceState | null;

	dockHosts: ShellDockHostMap;
};

export type ShellRuntimeAction =
	| {
			type: "SET_POPUP_DESIRED_OPEN";
			payload: boolean;
	  }
	| {
			type: "SET_POPUP_PHASE";
			payload: ShellPopupPhase;
	  }
	| {
			type: "SET_PENDING_POPUP_GENERATION";
			payload: number | null;
	  }
	| {
			type: "SET_FRESH_BOOT_REQUIRED_BEFORE_NEXT_OPEN";
			payload: boolean;
	  }
	| {
			type: "SET_MODAL_HAS_CONTENT";
			payload: boolean;
	  }
	| {
			type: "SET_DOCK_CONTENT_STATE";
			payload: EngagementDockContentState;
	  }
	| {
			type: "APPLY_DOCK_CONTENT_STATE";
			payload: {
				contentState: EngagementDockContentState;
				nextActiveDockTarget: ActiveDockTarget;
			};
	  }
	| {
			type: "SET_ACTIVE_DOCK_TARGET";
			payload: ActiveDockTarget;
	  }
	| {
			type: "INCREMENT_SUPPRESS_NEXT_PROTOCOL_CLOSE_TOKEN";
	  }
	| {
			type: "SET_DOCK_STATE";
			payload: DockSurfaceState | null;
	  }
	| {
			type: "SET_POPUP_STATE";
			payload: PopupSurfaceState | null;
	  }
	| {
			type: "SET_DOCK_HOST";
			payload: {
				target: ActiveDockTarget;
				host: HTMLDivElement | null;
			};
	  }
	| {
			type: "REQUEST_POPUP_OPEN";
	  }
	| {
			type: "REQUEST_POPUP_CLOSE";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "HANDLE_PROVIDER_PAUSE";
	  }
	| {
			type: "HANDLE_PROVIDER_RESUME";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "HANDLE_PROVIDER_MODAL_CLOSED";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "HANDLE_PROVIDER_RESET_REQUIRED";
			payload: {
				nextPhase: ShellPopupPhase;
				reason: EngagementProviderResetReason;
			};
	  }
	| {
			type: "PREPARE_FRESH_BOOT_FOR_POPUP_OPEN";
			payload: {
				nextExpectedGeneration: number;
			};
	  }
	| {
			type: "WAIT_FOR_PROVIDER";
			payload: {
				generation: number;
			};
	  }
	| {
			type: "OPEN_POPUP_NOW";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "PROMOTE_PENDING_POPUP_TO_OPEN";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "CLEAR_PENDING_POPUP_AND_CLOSE";
			payload: {
				nextPhase: ShellPopupPhase;
			};
	  }
	| {
			type: "REQUEST_FRESH_PROVIDER_BOOT";
	  };

export function readInitialDockTarget(): ActiveDockTarget {
	const raw = (getQueryParam("captainup_dock") ?? "").trim().toLowerCase();

	if (raw === "mission" || raw === "tournament") {
		return raw as ActiveDockTarget;
	}

	return "tournament";
}

export function createInitialShellRuntimeState(): ShellRuntimeState {
	return {
		popupPhase: "closed",
		popupDesiredOpen: false,
		popupOpenIntentId: 0,
		pendingPopupGeneration: null,
		freshBootRequiredBeforeNextOpen: false,

		modalHasContent: false,
		dockContentState: {
			missionHasContent: false,
			tournamentHasContent: false,
		},

		activeDockTarget: readInitialDockTarget(),
		suppressNextProtocolCloseToken: 0,

		dockState: null,
		popupState: null,

		dockHosts: {
			mission: null,
			tournament: null,
		},
	};
}

export function resolveCloseTargetPhase(
	popupState: PopupSurfaceState | null
): ShellPopupPhase {
	return popupState?.granted ? "closing" : "closed";
}

export function shellRuntimeReducer(
	state: ShellRuntimeState,
	action: ShellRuntimeAction
): ShellRuntimeState {
	switch (action.type) {
		case "SET_POPUP_DESIRED_OPEN":
			if (state.popupDesiredOpen === action.payload) {
				return state;
			}

			return {
				...state,
				popupDesiredOpen: action.payload,
			};

		case "SET_POPUP_PHASE":
			if (state.popupPhase === action.payload) {
				return state;
			}

			return {
				...state,
				popupPhase: action.payload,
			};

		case "SET_PENDING_POPUP_GENERATION":
			if (state.pendingPopupGeneration === action.payload) {
				return state;
			}

			return {
				...state,
				pendingPopupGeneration: action.payload,
			};

		case "SET_FRESH_BOOT_REQUIRED_BEFORE_NEXT_OPEN":
			if (state.freshBootRequiredBeforeNextOpen === action.payload) {
				return state;
			}

			return {
				...state,
				freshBootRequiredBeforeNextOpen: action.payload,
			};

		case "SET_MODAL_HAS_CONTENT":
			if (state.modalHasContent === action.payload) {
				return state;
			}

			return {
				...state,
				modalHasContent: action.payload,
			};

		case "SET_DOCK_CONTENT_STATE":
			if (
				state.dockContentState.missionHasContent ===
					action.payload.missionHasContent &&
				state.dockContentState.tournamentHasContent ===
					action.payload.tournamentHasContent
			) {
				return state;
			}

			return {
				...state,
				dockContentState: action.payload,
			};

		case "APPLY_DOCK_CONTENT_STATE": {
			const nextContentState = action.payload.contentState;
			const nextActiveDockTarget = action.payload.nextActiveDockTarget;

			const contentUnchanged =
				state.dockContentState.missionHasContent ===
					nextContentState.missionHasContent &&
				state.dockContentState.tournamentHasContent ===
					nextContentState.tournamentHasContent;

			const targetUnchanged =
				state.activeDockTarget === nextActiveDockTarget;

			if (contentUnchanged && targetUnchanged) {
				return state;
			}

			return {
				...state,
				dockContentState: nextContentState,
				activeDockTarget: nextActiveDockTarget,
			};
		}

		case "SET_ACTIVE_DOCK_TARGET":
			if (state.activeDockTarget === action.payload) {
				return state;
			}

			return {
				...state,
				activeDockTarget: action.payload,
			};

		case "INCREMENT_SUPPRESS_NEXT_PROTOCOL_CLOSE_TOKEN":
			return {
				...state,
				suppressNextProtocolCloseToken:
					state.suppressNextProtocolCloseToken + 1,
			};

		case "SET_DOCK_STATE":
			if (state.dockState === action.payload) {
				return state;
			}

			return {
				...state,
				dockState: action.payload,
			};

		case "SET_POPUP_STATE":
			if (state.popupState === action.payload) {
				return state;
			}

			return {
				...state,
				popupState: action.payload,
			};

		case "SET_DOCK_HOST": {
			const currentHost = state.dockHosts[action.payload.target] ?? null;

			if (currentHost === action.payload.host) {
				return state;
			}

			return {
				...state,
				dockHosts: {
					...state.dockHosts,
					[action.payload.target]: action.payload.host,
				},
			};
		}

		case "REQUEST_POPUP_OPEN":
			return {
				...state,
				popupDesiredOpen: true,
				popupOpenIntentId: state.popupOpenIntentId + 1,
			};

		case "REQUEST_POPUP_CLOSE":
			return {
				...state,
				popupDesiredOpen: false,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "HANDLE_PROVIDER_PAUSE":
			return {
				...state,
				popupDesiredOpen: true,
				popupOpenIntentId: state.popupOpenIntentId + 1,
			};

		case "HANDLE_PROVIDER_RESUME":
			return {
				...state,
				popupDesiredOpen: false,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "HANDLE_PROVIDER_MODAL_CLOSED":
			return {
				...state,
				suppressNextProtocolCloseToken:
					state.suppressNextProtocolCloseToken + 1,
				popupDesiredOpen: false,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "HANDLE_PROVIDER_RESET_REQUIRED":
			return {
				...state,
				popupDesiredOpen: false,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
				freshBootRequiredBeforeNextOpen: true,
			};

		case "PREPARE_FRESH_BOOT_FOR_POPUP_OPEN":
			return {
				...state,
				pendingPopupGeneration: action.payload.nextExpectedGeneration,
				freshBootRequiredBeforeNextOpen: false,
				popupPhase: "waiting_fresh_boot",
			};

		case "WAIT_FOR_PROVIDER":
			return {
				...state,
				pendingPopupGeneration: action.payload.generation,
				popupPhase: "waiting_provider",
			};

		case "OPEN_POPUP_NOW":
			return {
				...state,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "PROMOTE_PENDING_POPUP_TO_OPEN":
			return {
				...state,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "CLEAR_PENDING_POPUP_AND_CLOSE":
			return {
				...state,
				pendingPopupGeneration: null,
				popupPhase: action.payload.nextPhase,
			};

		case "REQUEST_FRESH_PROVIDER_BOOT":
			return {
				...state,
				pendingPopupGeneration: null,
				freshBootRequiredBeforeNextOpen: false,
			};
	}
}

export type ShellRuntimeSetters = {
	setActiveDockTarget: Dispatch<SetStateAction<ActiveDockTarget>>;
	setPopupVisible: Dispatch<SetStateAction<boolean>>;
	setDockState: Dispatch<SetStateAction<DockSurfaceState | null>>;
	setPopupState: Dispatch<SetStateAction<PopupSurfaceState | null>>;
	setDockHost: (
		target: ActiveDockTarget
	) => Dispatch<SetStateAction<HTMLDivElement | null>>;
};
